import { useQueryClient } from '@tanstack/react-query';

import { getExperimentVariables } from '../getExperimentVariables';
import {
  type AdditionalAttributes,
  type OpzlyExperimentsWithVariables,
  type OpzlyExperimentVariables,
} from '../types';
import { useExperiments } from '../useExperiments';

type UseExperimentOptions = {
  deferred?: boolean;
  additionalAttributes?: AdditionalAttributes;
};

export function useExperimentWithVariables<
  T extends OpzlyExperimentsWithVariables,
>(
  experiment: T,
  defaultVariables: OpzlyExperimentVariables<T>,
  options: { deferred: true; additionalAttributes?: AdditionalAttributes }
): OpzlyExperimentVariables<T> & {
  bucket: (overrideAttributes?: AdditionalAttributes) => void;
};
export function useExperimentWithVariables<
  T extends OpzlyExperimentsWithVariables,
>(
  experiment: T,
  defaultVariables: OpzlyExperimentVariables<T>,
  options?: { deferred?: false; additionalAttributes?: AdditionalAttributes }
): OpzlyExperimentVariables<T>;
export function useExperimentWithVariables<
  T extends OpzlyExperimentsWithVariables,
>(
  experiment: T,
  defaultVariables: OpzlyExperimentVariables<T>,
  { deferred = false, additionalAttributes }: UseExperimentOptions = {}
): OpzlyExperimentVariables<T> & {
  bucket: ((overrideAttributes?: AdditionalAttributes) => void) | undefined;
} {
  const queryClient = useQueryClient();

  const experimentResult = useExperiments([experiment], {
    deferred,
    additionalAttributes,
  });

  const bucket = Array.isArray(experimentResult)
    ? experimentResult[1]
    : undefined;

  const flagVariables = getExperimentVariables({
    queryClient,
    experiment,
    fallback: defaultVariables,
  });

  return { ...flagVariables, bucket };
}
